import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, FormGroupDirective, NgForm, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher, MatRippleModule } from '@angular/material/core';

import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { MediaFile, User } from '@app/models';
import { AuthService, FileService, MaterialService, SharedUtilsService } from '@app/services';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

// interface PDFInfo {
//   pageImage: string;
//   blobObject: Object;
//   isSelected: boolean;
//   selectionNumber: number;
// }

// interface ImageInfo {
//   pageImage: string;
//   imageName: string;
//   imageObject: Object;
//   isSelected: boolean;
//   selectionNumber: number;
// }

@Component({
  selector: 'add-material-dialog',
  templateUrl: './add-material-dialog.component.html',
  styleUrl: './add-material-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRippleModule
  ]
})
export class AddMaterialDialogComponent implements OnInit {
  @Input() materialCategories: any = [];
  @ViewChild('file') file;
  @ViewChild('ownCoverImage') ownCoverImage;
  @ViewChild(MatStepper, { static: true }) stepper: MatStepper;

  public files: Set<File> = new Set();
  public files2: Set<File> = new Set();

  /********* Objects ***********/
  ckEditorConfig = {
    height: '150px',
    'toolbarGroups': [
      { name: 'document', groups: ['mode', 'document', 'doctools'] },
      { name: 'clipboard', groups: ['clipboard', 'undo'] },
      { name: 'editing', groups: ['find', 'selection', 'spellchecker', 'editing'] },
      { name: 'forms', groups: ['forms'] },
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi', 'paragraph'] },
      { name: 'links', groups: ['links'] },
      { name: 'insert', groups: ['insert'] },
      { name: 'styles', groups: ['styles'] },
      { name: 'colors', groups: ['colors'] },
      { name: 'tools', groups: ['tools'] },
      { name: 'others', groups: ['others'] },
      { name: 'about', groups: ['about'] }
    ],
    'removeButtons': 'Source,Save,Templates,Cut,Undo,Find,SelectAll,Scayt,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField,Strike,Subscript,Superscript,CopyFormatting,RemoveFormat,Outdent,Indent,Blockquote,CreateDiv,JustifyLeft,JustifyCenter,JustifyRight,JustifyBlock,BidiLtr,BidiRtl,Language,Anchor,Image,Flash,Table,HorizontalRule,Smiley,SpecialChar,PageBreak,Iframe,TextColor,BGColor,Maximize,ShowBlocks,About,Styles,Format,Font,FontSize,NewPage,Preview,Print,Copy,Paste,PasteText,PasteFromWord,Redo,Replace'
  };

  materialFormData: any = {
    title: '',
    description: '',
    categories: [],
    pdfPath: '',
    videoPath: '',
    fileType: '',
    fileImageArray: [],
    defaultImage: '',
  };

  selectedSnapshot: any = {};
  selectedImage: any = {};

  /********* Variables ***********/
  currentUser: User = null;
  editorContent: any;
  materialTOPassBack: any;
  informationFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  thirdFormGroup: FormGroup;
  fourthFormGroup: FormGroup;
  matcher = new MyErrorStateMatcher();
  screenSize: any;
  imageToShow: any;
  videoToShow: any;
  coverImages: string[] = []; // For images generated from selected file. PDF pages, video cover, or uploaded images.
  fileToPreview: any = null; // The main preview. First page of PDF, main image, or video src.
  tempMainFileName: string = null; // Name of the uploaded file.
  progress: any;
  progress2: any;
  errorMsg = '';
  fileImageName: any;
  filePathName: any;
  uploadValue: any;
  uploadedMaterial: any;
  displayedImage = '';
  defaultImageNumber: number;
  selectedCount: any;
  subCategoryToShow: any;
  descriptionLimit: number = 100;
  currentWordCount: number = 0;
  wordsLeft: number = 100; // Should be the same as descriptionLimit.
  labelPosition = 'after';
  uploadPath: string = 'materials';

  /********* Booleans ***********/
  isAdmin: boolean = false;
  saveHappened: boolean = false;
  checked = false;
  indeterminate = false;
  stopRepeatFireOfEvent = false;
  validForm = false;
  showLoader = false;
  maxLimitReached = false;
  subCategoryMissing: boolean = false;

  /********* Arrays ***********/
  originalCoverImages: File[] = [];
  imagesToUpload: File[] = [];
  currentCategories: any[] = [];
  currentSubCategories: any[] = [];
  filetypesToNotInclude: string[] = [];

  subCategorySelect = new FormControl();

  newMaterial: MediaFile;

  // stepper completion variables
  infoCompleted1: boolean = false;
  infoCompleted2: boolean = false;
  hasError = null;

  constructor(
    public dialogRef: MatDialogRef<AddMaterialDialogComponent>,
    private fb: FormBuilder,
    private _authService: AuthService,
    private _fileService: FileService,
    private _materialService: MaterialService,
    private _sharedService: SharedUtilsService,
    public _tosDialog: MatDialog
  ) {
    this.filetypesToNotInclude = this._sharedService?.nonSupportedFiles ?? [];
  }


  ngOnInit() {
    this.currentUser = this._authService.getCurrentlyLoggedInUser();
    if (this.currentUser?._id) {
      this.isAdmin = this._authService.checkForAdminPrivileges(this.currentUser);
    }

    this.newMaterial = new MediaFile();
    this.currentCategories.push({ category: null, subCategory: null, availableSubCategories: [] });

    this.secondFormGroup = this.fb.group({
      editorCtrl: new FormControl(this.editorContent, [Validators.required])
    });

    this.thirdFormGroup = this.fb.group({
      fileImageToPassCtrl: new FormControl('', [Validators.required]),
      otherFileToPassCtrl: new FormControl('', [Validators.required])
    });

    // this.fourthFormGroup = this.fb.group({
    //   copyrightCtrl: new FormControl('', [Validators.required])
    // });

    this.uploadValue = '1';
    this.defaultImageNumber = 1;

    this.updateDescriptionWordStats(); // Set description word counts.
  }


  addCategory(): void {
    this.currentCategories.push({ category: null, subCategory: null, availableSubCategories: [] });
    this.checkInformation();
  }


  removeGroup(cat) {
    const index: number = this.currentCategories.indexOf(cat);

    if (index !== -1) {
      this.currentCategories.splice(index, 1);
    }

    this.checkInformation();
  }


  checkForSubs(categoryToFilterOn, index) {
    this.currentSubCategories = [];
    this.currentCategories[index].availableSubCategories = [];

    if (categoryToFilterOn !== undefined) {
      this.currentCategories[index].category = categoryToFilterOn.name;
    } else {
      this.currentCategories[index].category = null;
    }

    if (categoryToFilterOn && this.currentCategories[index].category) {
      if (categoryToFilterOn.subCategories && categoryToFilterOn.subCategories.length) {
        // this category has subcategories..
        this.currentCategories[index].availableSubCategories = categoryToFilterOn.subCategories;
      }
    }

    this.checkInformation();
  }


  subCatChanged(subCategoryChosen, index) {
    this.currentCategories[index].subCategory = subCategoryChosen;
    this.checkInformation();
  }


  onDescriptionKeydown(_event: KeyboardEvent) {
    this.updateDescriptionWordStats();
    if (this.maxLimitReached && _event?.key !== 'Backspace') _event.preventDefault();
  }


  updateDescriptionWordStats() {
    const value = this.materialFormData?.description || '';
    const words = value.trim().length ? value.match(/\S+/g)?.length || 0 : 0;

    this.currentWordCount = words;
    this.wordsLeft = Math.max(this.descriptionLimit - words, 0);
    this.maxLimitReached = words >= this.descriptionLimit;
  }


  checkInformation() {
    this.hasError = { messages: [] };
    let passesInfoCheck = true;

    // check if all required fields have been filled out
    if (!this.materialFormData.title || !this.materialFormData.title.length) { passesInfoCheck = false; }

    if (this.currentCategories === null || !this.currentCategories || !this.currentCategories.length) {
      passesInfoCheck = false;
      this.hasError.messages.push('Materials must have at least one category');
    }

    if (this.currentCategories && this.currentCategories.length) {
      // check if it has any category that has subcategories.
      let hasMissingCat = false;
      let hasMissingCatWithSubCats = false;

      this.currentCategories.forEach(_category => {
        if (_category.category === undefined || _category.category === null) {
          this.hasError.messages.push('You must choose a category, or remove the group');
          hasMissingCat = true;
        }
        if (_category.availableSubCategories && _category.availableSubCategories.length && (!_category.subCategory || _category.subCategory === null)) {
          this.hasError.messages.push('Sub Category missing for ' + _category.category);
          hasMissingCatWithSubCats = true;
        }
      });

      if (hasMissingCat || hasMissingCatWithSubCats) { passesInfoCheck = false; }
    }

    if (!this.hasError.messages.length) {
      if (this.currentCategories && this.currentCategories.length && this.currentCategories[0].category) {
        this.materialFormData.categories = this.currentCategories.map(_cc => {
          return { name: _cc.category, subCategory: _cc.subCategory };
        });
      } else {
        passesInfoCheck = false;
      }
    }

    this.infoCompleted1 = passesInfoCheck;
    this.subCategoryMissing = !passesInfoCheck;
  }


  checkDescription() {
    let passesInfoCheck = true;
    this.infoCompleted2 = true;

    if (!this.materialFormData.description || this.materialFormData.description === '') {
      passesInfoCheck = false;
    }
    this.infoCompleted2 = passesInfoCheck;
    this.validForm = passesInfoCheck;
    // console.log('Valid form: ', this.validForm);
  }


  addFiles() {
    this.file.nativeElement.click();
  }


  /* Reset array and variable values */
  resetArraysAndValues() {
    this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue('');
    this.thirdFormGroup.controls['otherFileToPassCtrl'].setValue('');

    this.fileToPreview = null;
    this.coverImages = [];
    this.tempMainFileName = null;
    this.uploadValue = '1';

    this.showLoader = false;

    this.files = new Set();
    this.files2 = new Set();

    this.materialFormData.defaultImage = '';
    this.materialFormData.fileImageArray = [];
    this.materialFormData.pdfPath = '';
    this.materialFormData.videoPath = '';

    this.clearSelections();
  }


  clearSelections() {
    this.selectedCount = 0;
    this.imagesToUpload = [];
  }


  /* A file was selected from drag n drop box */
  async onFilesAdded(_event: any) {
    this.resetArraysAndValues();

    this.showLoader = true; // Start loader for any file processing.

    try {
      let fileUploaded: File;
      const files: { [key: string]: File } = this.file?.nativeElement?.files ?? [];

      // If no files, reset default image and return early.
      if (!files?.length) {
        this.materialFormData.defaultImage = '';
        return;
      }

      for (const key in files) {
        if (!isNaN(parseInt(key, 0))) {
          fileUploaded = files[key];
          this.files.add(fileUploaded);
          break;
        }
      }

      if (!fileUploaded) return;

      this.tempMainFileName = fileUploaded.name;
      const extension = this.tempMainFileName.split('.').pop()?.toLowerCase();

      // If not an allowed file type reset values, show message, and return early.
      if (this.filetypesToNotInclude.includes(extension)) {
        this.resetArraysAndValues();
        this._sharedService.showErrorDialog('File type not accepted. Please upload an image, PDF, or video.');
        return;
      }

      // Handle file types.
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        this.materialFormData.fileType = 'Image';
        await this.handleImagePreview(fileUploaded);
      } else if (extension === 'pdf') {
        this.materialFormData.fileType = 'PDF';
        await this.handlePdfPreview(fileUploaded);
      } else {
        this.materialFormData.fileType = 'Video';
        await this.handleVideoPreview(fileUploaded);
      }
    } catch (_err) {
      console.log('Error handling uploaded file: ', _err);
    }

    this.checkFormValidation();
    this.showLoader = false; // Stop loader after all processing.
  }


  async handleImagePreview(_file: File) {
    if (!_file) return;

    const fileDataUrl = await this._fileService.readFileAsDataUrl(_file);

    if (!fileDataUrl?.length) {
      console.log('No preview data for file: ', _file?.name);
      return; // Exit early if file couldn't be read.
    }

    this.fileToPreview = fileDataUrl;
    this.coverImages = [this.fileToPreview];

    this.materialFormData.defaultImage = this.tempMainFileName;
    this.materialFormData.fileImageArray = [];

    this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue(this.materialFormData.defaultImage);
    this.thirdFormGroup.controls['otherFileToPassCtrl'].setValue('none');
  }


  async handlePdfPreview(_file: File) {
    if (!_file) return; // If no file return early.

    // Store PDF path. Don't need FileReader here since previews come from generated images.
    this.materialFormData.pdfPath = this.tempMainFileName;
    this.thirdFormGroup.controls['otherFileToPassCtrl'].setValue(this.materialFormData.pdfPath);

    try {
      // Generate image files from PDF.
      const imageFiles: File[] = await this._fileService.generatePdfImageArray(_file);
      // console.log('imageFiles: ', imageFiles);

      if (!imageFiles?.length) return;

      this.originalCoverImages = imageFiles;
      this.imagesToUpload = [...imageFiles];

      this.files2.clear(); // Clear file set.

      for (const _imageFile of imageFiles) {
        this.files2.add(_imageFile); // Real file for upload.
      }

      this.updateCoverImagesPreview();

      // Wire material data.
      this.materialFormData.defaultImage = imageFiles[0]?.name ?? '';
      this.materialFormData.fileImageArray = imageFiles.map(_f => _f?.name);
      this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue(this.materialFormData.defaultImage);
    } catch (_err) {
      console.log('Error handling PDF preview: ', _err);
    }
  }


  async handleVideoPreview(_file: File) {
    if (!_file) return;

    const fileDataUrl = await this._fileService.readFileAsDataUrl(_file);

    if (!fileDataUrl?.length) {
      console.log('No preview data for file: ', _file?.name);
      return; // Exit early if file couldn't be read
    }

    this.fileToPreview = fileDataUrl;

    // Store video path.
    this.materialFormData.videoPath = this.tempMainFileName;
    this.thirdFormGroup.controls['otherFileToPassCtrl'].setValue(this.materialFormData.videoPath);

    try {
      const thumbnailFile = await this._fileService.generateVideoThumbnail(_file); // Generate thumbnail file.
      // console.log('thumbnailFile: ', thumbnailFile);

      if (thumbnailFile) {
        this.originalCoverImages = [thumbnailFile];
        this.imagesToUpload = [thumbnailFile];

        this.files2.clear();
        this.files2.add(thumbnailFile); // Push real generated image file to second file array to upload.

        this.updateCoverImagesPreview();

        // Wire material data
        this.materialFormData.defaultImage = thumbnailFile?.name ?? '';
        this.materialFormData.fileImageArray = [thumbnailFile?.name];
        this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue(this.materialFormData.defaultImage);
      }
    } catch (_err) {
      console.log('Error generating video thumbnail: ', _err);
    }
  }


  updateCoverImagesPreview() {
    this.coverImages.forEach(_url => {
      if (_url?.startsWith('blob:')) URL.revokeObjectURL(_url);
    });

    const imagesToUpload = this.imagesToUpload?.length ? this.imagesToUpload : [];
    this.coverImages = imagesToUpload.map(_f => URL.createObjectURL(_f));
    // console.log('Cover Images: ', this.coverImages);

    // Update preview file if these types.
    if (['Image', 'PDF'].includes(this.materialFormData?.fileType)) this.fileToPreview = this.coverImages[0] ?? null;
  }


  onOwnCoverImageUpload(event: any) {
    const files: FileList = this.ownCoverImage.nativeElement.files;

    if (!files || files.length === 0) {
      this.uploadValue = '1'; // restore option 1.
      this.checkUploadThumbnailValue();
      return;
    }

    this.imagesToUpload = [];
    this.files2.clear();

    Array.from(files).forEach(file => {
      this.imagesToUpload.push(file);
      this.files2.add(file);
    });

    this.updateCoverImagesPreview();

    // Wire material data.
    this.materialFormData.defaultImage = this.imagesToUpload[0]?.name ?? '';
    this.materialFormData.fileImageArray = this.imagesToUpload.map(_f => _f?.name);
    this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue(this.materialFormData.defaultImage);
  }


  checkUploadThumbnailValue() {
    // Set original values from main upload back.
    if (this.uploadValue === '1') {
      this.stopRepeatFireOfEvent = false;
      this.imagesToUpload = [...this.originalCoverImages];
      this.files2.clear();
      this.imagesToUpload.forEach(f => this.files2.add(f));
      this.updateCoverImagesPreview();

      // Wire material data.
      this.materialFormData.defaultImage = this.imagesToUpload[0]?.name ?? '';
      this.materialFormData.fileImageArray = this.imagesToUpload.map(_f => _f?.name);
      this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue(this.materialFormData.defaultImage);
    }

    // User will upload their own cover images.
    if (this.uploadValue === '2' && !this.stopRepeatFireOfEvent) {
      this.stopRepeatFireOfEvent = true;
      this.thirdFormGroup.controls['fileImageToPassCtrl'].setValue('');
      this.imagesToUpload = [];
      this.files2.clear();
      this.coverImages = [];
    }

    // console.log('this.files2: ', this.files2);
    // console.log('imagesToUpload: ', this.imagesToUpload);
  }


  imageSelected(_image) {
    // console.log('Selected Image: ', _image);

    if (this.materialFormData.fileType !== 'Video') {
      this.fileToPreview = _image;
    }
  }


  async showTermsDialog() {
    // Caller is commented out in view. If we add back then add a dialog to use here.
  }


  async saveMaterial(): Promise<MediaFile | null> {
    // console.log('Material Form Data before save: ', this.materialFormData);
    this.showLoader = true; // Start loader for processing.

    // First, upload all files (main + cover images)
    const uploadsOk = await this.processFiles();
    if (!uploadsOk) {
      this._sharedService.showErrorDialog('Error uploading material files. Save Cancelled.', 6000);
      this.showLoader = false; // Stop loader due to error.
      return null;
    }

    // Build new material
    const newMaterial = new MediaFile();
    newMaterial.title = this.materialFormData?.title ?? '';
    newMaterial.description = this.materialFormData?.description ?? '';
    newMaterial.categories = this.materialFormData?.categories ?? [];
    newMaterial.uploaded = new Date();

    newMaterial.user = this.currentUser?._id ?? '';
    newMaterial.fileType = this.materialFormData?.fileType ?? '';
    newMaterial.defaultImage = this.materialFormData?.defaultImage ?? '';
    newMaterial.fileImageArray = this.materialFormData?.fileImageArray ?? [];

    if (this.materialFormData?.pdfPath?.length) {
      newMaterial.filePath = this.materialFormData.pdfPath;
    } else if (this.materialFormData?.videoPath?.length) {
      newMaterial.filePath = this.materialFormData.videoPath;
    }

    // console.log('Would save: ', newMaterial);

    try {
      const savedMaterial = await lastValueFrom(this._materialService.saveMaterial(newMaterial));
      if (savedMaterial?._id) {
        this.newMaterial = savedMaterial;
        this.confirmCreate(); // Close dialog and return success if needed
      } else {
        this._sharedService.showErrorDialog('Error saving material. Save Cancelled.', 6000);
        this.showLoader = false; // Stop loader failed save.
      }
    } catch (err) {
      console.error('Error creating material:', err);
      this._sharedService.showErrorDialog('Error saving material. Save Cancelled.', 6000);
      this.showLoader = false; // Stop loader due to error.
    }
  }


  async processFiles(): Promise<boolean> {
    try {
      // Upload main file.
      if (this.files?.size) {
        const mainFile = Array.from(this.files)[0]; // Only 1 main file.
        const mainLink = await this.uploadFileAndGetLink(mainFile);

        if (!mainLink?.length) return false;

        if (this.materialFormData.fileType === 'PDF') {
          this.materialFormData.pdfPath = mainLink;
        } else if (this.materialFormData.fileType === 'Video') {
          this.materialFormData.videoPath = mainLink;
        } else if (this.materialFormData.fileType === 'Image') {
          this.materialFormData.defaultImage = mainLink;
          this.materialFormData.fileImageArray = [mainLink];
        }
      }

      // Upload cover images and thumbnails.
      if (this.files2?.size && this.materialFormData.fileType !== 'Image') {
        const imageLinks: string[] = [];

        for (const _file of this.files2) {
          const link = await this.uploadFileAndGetLink(_file);
          if (link?.length) imageLinks.push(link);
        }

        if (!imageLinks.length) return false;

        this.materialFormData.fileImageArray = imageLinks;
        this.materialFormData.defaultImage = imageLinks[0];
      }

      return true;
    } catch (err) {
      console.error('processFiles error:', err);
      return false;
    }
  }


  uploadFileAndGetLink(_file: File): Promise<string | null> {
    return new Promise(async (resolve) => {
      const pathSpecified = 'materials';

      if (_file) {
        try {
          const uploadResult = await lastValueFrom(this._fileService.uploadToSpecifedFolder(new Set([_file]), pathSpecified));
          const resultsPart: any = (uploadResult.length) ? uploadResult[0] : uploadResult;
          const linkToReturn = resultsPart?.body?.link ?? null;

          resolve(linkToReturn);
        } catch (_err) {
          console.error(`Error uploading image ${_file?.name}`, _err);
          resolve(null);
        }
      } else {
        console.error('No file to upload. Skipping upload.');
        resolve(null);
      }
    });
  }


  confirmCreate() {
    this.files = new Set();
    this.files2 = new Set();

    this.saveHappened = true;
    this.materialTOPassBack = this.newMaterial;

    this.closeDialog();
  }


  /* Cancel creation */
  unconfirmCreate() {
    this.saveHappened = false;
    this.materialTOPassBack = null;
    this.closeDialog();
  }


  /* Close dialog */
  closeDialog() {
    return this.dialogRef.close({ 'saveHappened': this.saveHappened, 'materialTOPassBack': this.materialTOPassBack });
  }


  /* Checking form values and setting it to data object */
  checkFormValidation() {
    const mf = this.materialFormData;
    let passesChecks = true;

    if (!mf?.title?.length || !mf?.description?.length || !this.editorContent?.length || !mf?.categories?.length || !mf?.defaultImage?.length) passesChecks = false;

    this.validForm = passesChecks;
  }
}